import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import {
  Account_RemoveAccountFromChatsMutation,
  Account_RemoveAccountFromChatsMutationVariables,
} from '@/types';
import { ActionSheet } from '@/components/action-sheet';
import { gql, useMutation } from '@apollo/client';
import { partnerSupplier } from '@/utils/environment';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useSignOut } from '@/hooks/use-sign-out';
import { useToasts } from '@/contexts/toast-context';

export const REMOVE_ACCOUNT_FROM_CHATS_MUTATION = gql`
  mutation Account_RemoveAccountFromChats(
    $businessOrganizationUUID: uuid!
    $employeeUUID: uuid!
  ) {
    removeSelfFromChats(
      business_organization_id: $businessOrganizationUUID
      employee_id: $employeeUUID
    ) {
      employee_id
    }
  }
`;

interface Props {
  onClose: () => void;
}

export const DeleteAccountActionSheet: React.FC<Props> = ({ onClose }) => {
  const signOut = useSignOut();
  const { showErrorToast } = useToasts();
  const { user } = useAuthenticatedUserContext();

  const [removeSelfFromChats, { loading }] = useMutation<
    Account_RemoveAccountFromChatsMutation,
    Account_RemoveAccountFromChatsMutationVariables
  >(REMOVE_ACCOUNT_FROM_CHATS_MUTATION);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  // Persist loading spinner when deleting user and logging out
  useEffect(() => {
    if (loading) {
      setIsDeletingUser(true);
    }
  }, [loading]);

  return (
    <ActionSheet
      actions={[i18n.t('delete_account'), i18n.t('cancel')]}
      isVisible
      loadingActionIndex={isDeletingUser ? 0 : null}
      message={i18n.t('delete_account.confirmation_message', {
        appName: partnerSupplier.fullName,
      })}
      onAction={async (_, index) => {
        if (index === 0) {
          Analytics.track('employee.delete_account_confirmation_button.press');
          try {
            await removeSelfFromChats({
              variables: {
                businessOrganizationUUID:
                  partnerSupplier.businessOrganizationUUID,
                employeeUUID: user.uuid,
              },
            });

            await signOut();
          } catch {
            setIsDeletingUser(false);
            showErrorToast();
          }
        }

        onClose();
      }}
      onClose={onClose}
      testID="delete-account-action-sheet"
      title={i18n.t('delete_account.confirmation_title')}
    />
  );
};
